import { REFRESH_REFUSALS } from '@featit/shared/credentials';
import type { MessageKey } from '@featit/shared/i18n/messages/index';

/**
 * Why a reader is looking at the sign-in page, and — before that — whether they should be (I-0464).
 *
 * ⛔ **The distinction this file exists for.** A refused refresh has two quite different meanings, and
 * treating them as one is what signed Jay out: the session is over, or *this holder's copy of the
 * secret* is out of date. A credential is held by a store, not by a process — every tab of an origin
 * shares one `localStorage` — so a tab that reads "not valid" and clears that store signs out every
 * other tab, including the one that refreshed successfully a moment earlier.
 *
 * The sentences are written here rather than sent by the server, for the reason `oauthSignIn.tsx`
 * gives: a message that crosses a URL is a message some page has to render, and a URL parameter
 * rendered as text is an injection surface. What crosses is a code from a closed vocabulary.
 */
export const SESSION_END_REASONS = {
  /** The credential was revoked, has expired, or its holder was deactivated. */
  ENDED: 'ended',
  /** Revoked because a secret it had already replaced came back (I-0394). */
  REUSE: 'reuse',
  /** Nothing in this store can be refreshed any more, and no other holder has anything better. */
  STALE: 'stale',
} as const;

export type SessionEndReasonType = (typeof SESSION_END_REASONS)[keyof typeof SESSION_END_REASONS];

export interface ISessionEndMessage {
  /** What happened, in the second person. */
  title: string;
  /** What to do about it. */
  action: string;
  /** The keys the sign-in page reads both under; `title` and `action` are their English (I-2362). */
  i18n: { title: MessageKey; action: MessageKey };
}

/**
 * ⛔ Total over the vocabulary, so adding a reason without writing its sentence fails the type check
 * — the alternative renders a bare slug on the one screen where somebody has no other way to find
 * out why they are looking at it.
 */
export const SESSION_END_MESSAGES: Record<SessionEndReasonType, ISessionEndMessage> = {
  [SESSION_END_REASONS.ENDED]: {
    title: 'Your session has ended.',
    i18n: { title: 'auth.sessionEnded.ended.title', action: 'auth.sessionEnded.ended.action' },
    action: 'Sign in again to pick up where you left off — nothing you had open was lost.',
  },
  [SESSION_END_REASONS.REUSE]: {
    title: 'This session was signed out to protect your account.',
    i18n: { title: 'auth.sessionEnded.reuse.title', action: 'auth.sessionEnded.reuse.action' },
    action:
      'A sign-in secret that had already been replaced was used again. Sign in again, and review ' +
      'your credentials in Settings — you can revoke any you do not recognise.',
  },
  [SESSION_END_REASONS.STALE]: {
    title: 'This browser could not renew your sign-in.',
    i18n: { title: 'auth.sessionEnded.stale.title', action: 'auth.sessionEnded.stale.action' },
    action: 'Sign in again to continue. Your other devices are unaffected.',
  },
};

function isSessionEndReason(value: unknown): value is SessionEndReasonType {
  return Object.values<string>(SESSION_END_REASONS).includes(value as string);
}

/** The sentence for a reason that arrived in a URL, or null for anything outside the vocabulary. */
export function sessionEndMessage(raw: string | null | undefined): ISessionEndMessage | null {
  return isSessionEndReason(raw) ? SESSION_END_MESSAGES[raw] : null;
}

function refusalCode(error: unknown): string | null {
  const code = (error as { response?: { data?: { code?: unknown } } })?.response?.data?.code;
  return typeof code === 'string' ? code : null;
}

// ⛔ A 401 only: every refresh refusal is one, and a 403 never ends a session (AGM-41).
function isAuthRefusal(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return status === 401;
}

/**
 * The session the **server** says is over, and null for everything else — including the refusal that
 * says only that this holder's secret is behind.
 *
 * ⚠️ A 401 carrying no code at all is read as an ending, which is what every refused refresh meant
 * before I-0464. A bundle is served by the deployment it talks to, so the two move together; a
 * desktop shell pointed at an older deployment is the case this keeps working, at the cost of the
 * behaviour it already had.
 */
export function sessionEndReason(error: unknown): SessionEndReasonType | null {
  if (!isAuthRefusal(error)) return null;
  switch (refusalCode(error)) {
    case REFRESH_REFUSALS.REVOKED_AFTER_REUSE:
      return SESSION_END_REASONS.REUSE;
    case REFRESH_REFUSALS.SESSION_ENDED:
      return SESSION_END_REASONS.ENDED;
    case REFRESH_REFUSALS.SECRET_UNKNOWN:
      return null;
    default:
      return SESSION_END_REASONS.ENDED;
  }
}

/**
 * The refusal that means *re-read the store*, not *sign out*: no live credential holds the secret
 * this holder presented, which is what another tab rotating past it looks like from here.
 */
export function isStaleSecretRefusal(error: unknown): boolean {
  return isAuthRefusal(error) && refusalCode(error) === REFRESH_REFUSALS.SECRET_UNKNOWN;
}

/**
 * I-2572 (MS-3): where a session end came from, as the desktop log names it — a closed vocabulary of
 * plain words, so a log line can say nothing else. Every path but the Sign out dialog is logged
 * (`endSession`). An unnamed sign-out is logged too, as `sign-out`.
 */
export const SESSION_END_PATHS = {
  SIGN_OUT_DIALOG: 'sign-out-dialog',
  SIGN_OUT: 'sign-out',
  REFRESH_REFUSED: 'refresh-refused',
  SESSION_CHECK_REFUSED: 'session-check-refused',
  ENDED_ELSEWHERE: 'ended-elsewhere',
  ANOTHER_PERSON: 'another-person',
  CREDENTIAL_REVOKED: 'credential-revoked',
  DEVICE_REVOKED: 'device-revoked',
  ACCOUNT_DELETED: 'account-deleted',
  TERMS_DECLINED: 'terms-declined',
  MOBILE_PAIRING: 'mobile-pairing',
  INVITATION_SWITCH: 'invitation-switch',
} as const;

export type SessionEndPathType = (typeof SESSION_END_PATHS)[keyof typeof SESSION_END_PATHS];

/**
 * I-2572: why, beside the server's reasons. `no-secret` is a session this page found it could not
 * renew by itself — nothing the server said — and it never clears the desktop keychain.
 */
export const SESSION_END_LOCAL_REASONS = {
  NO_SECRET: 'no-secret',
  PERSON: 'person',
  REFUSED: 'refused',
  STORAGE_CLEARED: 'storage-cleared',
  PERSON_CHANGED: 'person-changed',
} as const;

export type SessionEndLogReasonType =
  | SessionEndReasonType
  | (typeof SESSION_END_LOCAL_REASONS)[keyof typeof SESSION_END_LOCAL_REASONS];
